import React, { useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Menu } from "primereact/menu";
import { ConfirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { paginatorTemplate } from "../../components/OtherComponents/PaginatorTemplate";
import {
  deleteWarehouse,
  getWarehouses,
  getSpecificWarehouse,
  addWarehouse,
  updateWarehouse,
} from "../../store/AsyncMethods/WarehouseMethod";
import AddWarehouseForm from "./AddWarehouseForm";
import { RESET_SPECIFIC_WAREHOUSE } from "../../store/Types/WarehouseTypes";

export default function WarehousesTable() {
  const { allWarehouses, specificWarehouse } = useSelector(
    (state) => state.WarehouseReducer
  );

  const menu = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const dispatch = useDispatch();
  const [showDelDialog, setShowDelDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setSearchTerm(value);
  };

  const renderHeader = () => {
    return (
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-700">Search:</span>
          <InputText
            value={searchTerm}
            onChange={onGlobalFilterChange}
            placeholder="Search warehouses..."
            className="w-full md:w-72"
          />
        </div>
        <Button
          label="Add Warehouse"
          icon="pi pi-plus"
          className="p-primary-btn"
          onClick={() => setShowAddDialog(true)}
        />
      </div>
    );
  };

  const header = renderHeader();

  let items = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        dispatch(getSpecificWarehouse(selectedItem?.id)).then(() => {
          setShowEditDialog(true);
        });
      },
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        setShowDelDialog(true);
      },
    },
  ];

  const actionBtn = (rowData) => (
    <React.Fragment>
      <div className=" ">
        <i
          className="pi pi-cog text-secondary cursor-pointer"
          onClick={(e) => {
            menu.current.toggle(e);
            setSelectedItem(rowData);
          }}
        ></i>
        <Menu model={items} popup ref={menu} className="p-0 z-50" />
      </div>
    </React.Fragment>
  );

  const statusBodyTemplate = (rowData) => {
    return (
      <Tag
        value={rowData.is_active ? "Active" : "Inactive"}
        className="capitalize"
        pt={{
          root: {
            className: rowData.is_active ? "bg-green-500" : "bg-red-500",
          },
        }}
      />
    );
  };

  const cityBodyTemplate = (rowData) => {
    return (
      <div className="flex flex-col">
        <span className="font-semibold text-gray-800">{rowData.city}</span>
        <span className="text-xs text-gray-500">{rowData.phone}</span>
      </div>
    );
  };

  const addressBodyTemplate = (rowData) => {
    return (
      <div className="max-w-xs">
        <span className="text-sm text-gray-700">{rowData.address}</span>
      </div>
    );
  };

  const handleDelete = () => {
    if (selectedItem) {
      dispatch(deleteWarehouse(selectedItem.id)).then((success) => {
        if (success) {
          dispatch(getWarehouses());
          setShowDelDialog(false);
          setSelectedItem(null);
        }
      });
    }
  };

  const handleAddSuccess = () => {
    setShowAddDialog(false);
    dispatch(getWarehouses());
  };

  const handleEditSuccess = () => {
    setShowEditDialog(false);
    dispatch(getWarehouses());
    dispatch({ type: RESET_SPECIFIC_WAREHOUSE });
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Warehouse Management</h1>
      </div>

      {/* Data Table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow">
        <DataTable
          value={allWarehouses}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate={paginatorTemplate}
          filters={filters}
          filterDisplay="row"
          globalFilterFields={["name", "city", "address", "phone"]}
          header={header}
          emptyMessage="No warehouses found."
          className="p-datatable-sm"
        >
          <Column
            field="name"
            sortable
            header="Warehouse Name"
            bodyClassName="!py-3 font-semibold text-gray-800"
          />
          <Column
            field="city"
            sortable
            header="City & Phone"
            body={cityBodyTemplate}
            bodyClassName="!py-3"
          />
          <Column
            field="address"
            header="Address"
            body={addressBodyTemplate}
            bodyClassName="!py-3"
            style={{ minWidth: "200px" }}
          />
          <Column
            field="is_active"
            sortable
            header="Status"
            body={statusBodyTemplate}
            bodyClassName="!py-3"
          />
          <Column header="Action" body={actionBtn} bodyClassName="!py-3" />
        </DataTable>
      </div>

      {/* Add Warehouse Dialog */}
      <Dialog
        header="Add Warehouse"
        visible={showAddDialog}
        style={{ width: "50vw" }}
        onHide={() => setShowAddDialog(false)}
        modal
      >
        <AddWarehouseForm
          onSuccess={handleAddSuccess}
          onCancel={() => setShowAddDialog(false)}
        />
      </Dialog>

      {/* Edit Warehouse Dialog */}
      <Dialog
        header="Edit Warehouse"
        visible={showEditDialog}
        style={{ width: "50vw" }}
        onHide={() => {
          setShowEditDialog(false);
          dispatch({ type: RESET_SPECIFIC_WAREHOUSE });
        }}
        modal
      >
        {specificWarehouse && (
          <AddWarehouseForm
            warehouse={specificWarehouse}
            onSuccess={handleEditSuccess}
            onCancel={() => {
              setShowEditDialog(false);
              dispatch({ type: RESET_SPECIFIC_WAREHOUSE });
            }}
          />
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        visible={showDelDialog}
        onHide={() => setShowDelDialog(false)}
        message={`Are you sure you want to delete "${selectedItem?.name}"?`}
        header="Confirm Delete"
        icon="pi pi-exclamation-triangle"
        accept={handleDelete}
        reject={() => setShowDelDialog(false)}
      />
    </div>
  );
}
